import { motion } from 'framer-motion';
import { Check, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const Row = ({ item, idx, last, prefix, onToggle, sortable }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id, disabled: !sortable });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative flex items-center bg-[#0A0A0A] ${
        !last ? 'border-b border-white/[0.06]' : ''
      } ${isDragging ? 'z-10 shadow-[0_8px_24px_rgba(0,0,0,0.6)] border border-sky-400/30 rounded-2xl' : ''}`}
    >
      <button
        type="button"
        onClick={() => onToggle(item)}
        data-testid={`nexa-${prefix}-item-${idx}`}
        className="flex-1 min-w-0 h-16 pl-5 flex items-center gap-4 text-left"
      >
        <motion.span
          animate={{
            backgroundColor: item.done ? '#38BDF8' : 'rgba(255,255,255,0)',
            borderColor: item.done ? '#38BDF8' : 'rgba(255,255,255,0.25)',
          }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          className="w-7 h-7 rounded-full border flex items-center justify-center shrink-0"
        >
          {item.done && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 600, damping: 20 }}
            >
              <Check className="w-4 h-4 text-black" strokeWidth={3} />
            </motion.span>
          )}
        </motion.span>
        <span
          className={`text-base transition-colors duration-200 ${
            item.done ? 'text-white/35 line-through' : 'text-white'
          }`}
        >
          {item.text}
        </span>
        {item.supplement && item.time && (
          <span className="text-[11px] font-mono text-sky-400/70 shrink-0 pr-4">{item.time}</span>
        )}
      </button>
      {sortable && (
        <span
          {...attributes}
          {...listeners}
          data-testid={`nexa-${prefix}-drag-${idx}`}
          aria-label="Reorder"
          className="px-4 h-16 flex items-center text-white/20 cursor-grab active:cursor-grabbing active:text-sky-400 shrink-0"
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="w-4 h-4" strokeWidth={1.8} />
        </span>
      )}
    </div>
  );
};

export const RoutineChecklist = ({ items, onToggle, onReorder, prefix = 'routine' }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
  );
  const handleEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(items, oldIdx, newIdx));
  };
  return (
    <div
      className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden"
      data-testid={`nexa-${prefix}-list`}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {items.map((item, idx) => (
            <Row
              key={item.id}
              item={item}
              idx={idx}
              last={idx === items.length - 1}
              prefix={prefix}
              onToggle={onToggle}
              sortable={Boolean(onReorder) && !item.supplement}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};
